import { useState, useEffect } from 'react';
import { Link, useParams } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import {
  ArrowLeft, ArrowRight, Clock, Tag, Loader2, Activity, BookOpen
} from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const categoryColors = {
  Science: 'bg-blue-50 text-white border-blue-100',
  Growth: 'bg-black text-white border-white/10',
  Relationships: 'bg-pink-50 text-pink-600 border-pink-100',
  Wellness: 'bg-amber-spark/10 text-amber-600 border-amber-100',
  Career: 'bg-violet-50 text-violet-600 border-violet-100'
};

const BlogList = () => {
  const [posts, setPosts] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetch(`${API}/blog`)
      .then(r => r.json())
      .then(d => setPosts(d.posts))
      .catch(() => {})
      .finally(() => setLoading(false));
  }, []);

  if (loading) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <Loader2 className="h-8 w-8 animate-spin text-white" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background">
      {/* Header */}
      <div className="bg-black/85 backdrop-blur-xl border-b border-white/10/60">
        <div className="max-w-6xl mx-auto px-6 py-4 flex items-center justify-between">
          <Link to="/" className="inline-flex items-center gap-2 text-smoke hover:text-white transition-colors" data-testid="blog-back-home">
            <ArrowLeft className="h-4 w-4" /><span className="text-sm">Home</span>
          </Link>
          <div className="flex items-center gap-2">
            <div className="w-8 h-8 rounded-lg bg-plum-voltage flex items-center justify-center">
              <Activity className="h-4 w-4 text-white" strokeWidth={2} />
            </div>
            <span className="font-bold text-white tracking-tight">Mindpuls<span className="underline decoration-slate-600 decoration-2 underline-offset-2">eI</span></span>
          </div>
        </div>
      </div>

      {/* Hero */}
      <div className="bg-gradient-to-r from-slate-900 to-slate-800 text-white">
        <div className="max-w-6xl mx-auto px-6 py-16">
          <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-black/10 text-sm font-medium mb-6 border border-white/10">
            <BookOpen className="h-3.5 w-3.5" /> Insights & Research
          </div>
          <h1 className="text-4xl sm:text-5xl font-extrabold tracking-tight mb-4" data-testid="blog-page-title">The EI Blog</h1>
          <p className="text-smoke text-lg max-w-lg">
            Explore the science of emotional intelligence and how it helps you live a better life and make better decisions.
          </p>
        </div>
      </div>

      {/* Posts grid */}
      <div className="max-w-6xl mx-auto px-6 py-14" data-testid="blog-list">
        {/* Featured post — first one large */}
        {posts.length > 0 && (
          <Link to={`/blog/${posts[0].slug}`} data-testid={`blog-card-${posts[0].slug}`}>
            <Card className="rounded-2xl border-white/10 card-hover overflow-hidden mb-8 group">
              <div className="grid md:grid-cols-2">
                <div className="h-64 md:h-auto overflow-hidden">
                  <img src={posts[0].image} alt={posts[0].title} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500" />
                </div>
                <CardContent className="p-8 flex flex-col justify-center">
                  <div className="flex items-center gap-3 mb-4">
                    <span className={`px-3 py-1 rounded-full text-xs font-medium border ${categoryColors[posts[0].category] || 'bg-black text-ash border-white/10'}`}>
                      {posts[0].category}
                    </span>
                    <span className="text-xs text-smoke flex items-center gap-1"><Clock className="h-3 w-3" />{posts[0].read_time}</span>
                  </div>
                  <h2 className="text-2xl font-extrabold text-white tracking-tight mb-3 group-hover:text-white transition-colors">{posts[0].title}</h2>
                  <p className="text-smoke text-sm leading-relaxed mb-4">{posts[0].excerpt}</p>
                  <span className="text-white text-sm font-medium inline-flex items-center gap-1">Read Article <ArrowRight className="h-3.5 w-3.5" /></span>
                </CardContent>
              </div>
            </Card>
          </Link>
        )}

        {/* Remaining posts */}
        <div className="grid md:grid-cols-2 lg:grid-cols-2 gap-5">
          {posts.slice(1).map((post) => (
            <Link key={post.slug} to={`/blog/${post.slug}`} data-testid={`blog-card-${post.slug}`}>
              <Card className="rounded-2xl border-white/10 card-hover overflow-hidden h-full group">
                <div className="h-48 overflow-hidden">
                  <img src={post.image} alt={post.title} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500" />
                </div>
                <CardContent className="p-6">
                  <div className="flex items-center gap-3 mb-3">
                    <span className={`px-2.5 py-1 rounded-full text-xs font-medium border ${categoryColors[post.category] || 'bg-black text-ash border-white/10'}`}>
                      {post.category}
                    </span>
                    <span className="text-xs text-smoke flex items-center gap-1"><Clock className="h-3 w-3" />{post.read_time}</span>
                  </div>
                  <h3 className="text-lg font-bold text-white tracking-tight mb-2 group-hover:text-white transition-colors">{post.title}</h3>
                  <p className="text-smoke text-sm leading-relaxed">{post.excerpt}</p>
                </CardContent>
              </Card>
            </Link>
          ))}
        </div>
      </div>
    </div>
  );
};

const BlogPost = () => {
  const { slug } = useParams();
  const [post, setPost] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetch(`${API}/blog/${slug}`)
      .then(r => r.json())
      .then(d => setPost(d))
      .catch(() => {})
      .finally(() => setLoading(false));
  }, [slug]);

  if (loading) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <Loader2 className="h-8 w-8 animate-spin text-white" />
      </div>
    );
  }

  if (!post) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <Card className="rounded-2xl border-white/10"><CardContent className="p-8 text-center">
          <p className="text-smoke mb-4">Post not found</p>
          <Link to="/blog"><Button className="rounded-full bg-plum-voltage hover:bg-black">Back to Blog</Button></Link>
        </CardContent></Card>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background">
      {/* Header */}
      <div className="bg-black/85 backdrop-blur-xl border-b border-white/10/60 sticky top-0 z-50">
        <div className="max-w-4xl mx-auto px-6 py-4 flex items-center justify-between">
          <Link to="/blog" className="inline-flex items-center gap-2 text-smoke hover:text-white transition-colors" data-testid="back-to-blog">
            <ArrowLeft className="h-4 w-4" /><span className="text-sm">All Articles</span>
          </Link>
          <div className="flex items-center gap-2">
            <div className="w-8 h-8 rounded-lg bg-plum-voltage flex items-center justify-center">
              <Activity className="h-4 w-4 text-white" strokeWidth={2} />
            </div>
            <span className="font-bold text-white tracking-tight">Mindpuls<span className="underline decoration-slate-600 decoration-2 underline-offset-2">eI</span></span>
          </div>
        </div>
      </div>

      {/* Hero image */}
      <div className="h-72 md:h-96 overflow-hidden relative">
        <img src={post.image} alt={post.title} className="w-full h-full object-cover" />
        <div className="absolute inset-0 bg-gradient-to-t from-black/50 to-transparent" />
      </div>

      {/* Article */}
      <div className="max-w-3xl mx-auto px-6 -mt-20 relative z-10" data-testid="blog-post-content">
        <Card className="rounded-2xl border-white/10 overflow-hidden">
          <CardContent className="p-8 md:p-12">
            <div className="flex items-center gap-3 mb-6">
              <span className={`px-3 py-1 rounded-full text-xs font-medium border ${categoryColors[post.category] || 'bg-black text-ash border-white/10'}`}>
                {post.category}
              </span>
              <span className="text-xs text-smoke flex items-center gap-1"><Clock className="h-3 w-3" />{post.read_time}</span>
            </div>

            <h1 className="text-3xl md:text-4xl font-extrabold text-white tracking-tight mb-6 leading-tight" data-testid="blog-post-title">{post.title}</h1>

            <div className="prose-custom">
              {post.content?.map((block, i) => {
                if (block.type === 'heading') {
                  return <h2 key={i} className="text-xl font-bold text-white mt-8 mb-3">{block.text}</h2>;
                }
                return <p key={i} className="text-ash leading-relaxed mb-4 text-[15px]">{block.text}</p>;
              })}
            </div>

            {/* Bottom CTA */}
            <div className="mt-10 pt-8 border-t border-white/10">
              <p className="text-sm text-smoke mb-4">Ready to discover your emotional intelligence level?</p>
              <div className="flex gap-3">
                <Link to="/login">
                  <Button className="rounded-full bg-plum-voltage hover:bg-black" data-testid="blog-cta-assess">
                    Take Free Assessment <ArrowRight className="ml-2 h-4 w-4" />
                  </Button>
                </Link>
                <Link to="/blog">
                  <Button variant="outline" className="rounded-full border-white/10" data-testid="blog-cta-more">
                    More Articles
                  </Button>
                </Link>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      <div className="h-16" />
    </div>
  );
};

const Blog = () => {
  const { slug } = useParams();
  if (slug) return <BlogPost />;
  return <BlogList />;
};

export default Blog;
